import React, { useEffect, useState } from 'react';
import { Placeholder, Text } from '@sitecore-jss/sitecore-jss-react';
import useErrorState from '../../../utils/errors/useErrorState';
import { errorMessages } from '../../../utils/errors/constants';
import { connect, useDispatch } from 'react-redux';
import { resolve } from '../../../utils/object/pathResolver';

const AddressPicker = ({ rendering, fields, displayErrors, addresses, postCode, defaultValue }) => {
  const [error, setError] = useErrorState(rendering.uid, fields.name?.value, fields);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [showManualAddress, setShowManualAddress] = useState(false);
  const dispatch = useDispatch();
  const errorClass = error && displayErrors ? ' vds-form__input--invalid' : '';

  const validateAddress = add => {
    if (!add || add === '') {
      setError(errorMessages.EMPTY);
      return false;
    }
    setError(null);
    return true;
  };

  const handleAddressUpdate = e => {
    const { value } = e.target;
    setSelectedAddress(value);

    if (!validateAddress(value)) {
      return;
    }
    const selectedAddressObj = addresses.entityAddressList.find(
      addr => [addr.line1, addr.line2, addr.line3, addr.city].filter(Boolean).join(', ') === value
    );

    if (selectedAddressObj) {
      const { line1, line2, city } = selectedAddressObj;
      if (validateAddress(value)) {
        const dispatchData = {
          type: fields.reduxDispatchType?.value,
          payload: {
            line1: line1 || '',
            line2: line2 || '',
            line3: city || '',
          },
        };
        dispatch(dispatchData);
      }
    }
  };

  const defaultAddress = addresses?.entityAddressList?.map(address =>
    [address.line1, address.line2, address.line3, address.city].filter(Boolean).join(', ')
  );

  useEffect(() => {
    const hasValidAddress =
      defaultValue && (defaultValue.line1 || defaultValue.line2 || defaultValue.line3);
    if (fields.required?.value && !hasValidAddress) {
      setError(errorMessages.EMPTY);
    } else {
      const valueString = [defaultValue?.line1, defaultValue?.line2, defaultValue?.line3]
        .filter(Boolean)
        .join(', ');
      setSelectedAddress(valueString);
    }

    return () => {
      setError(null);
    };
  }, []);

  return (
    <div className="vds-d2c-form-page__section form-center">
      <div className="vds-d2c-form-page__inner">
        <div className="vds-form__group vds-form-group--limited-width" name={fields.name?.value}>
          {showManualAddress ? (
            <Placeholder name="manual-address" rendering={rendering} />
          ) : (
            <>
              <div className="vds-form__group vds-form__group--limited-width">
                <Text tag="label" className="vds-form__label show" field={fields.titleLabel} />
                <select
                  className={`vds-form__input vds-form__input--select${errorClass}`}
                  name={`${fields.name?.value}-address-dropdown`}
                  data-testid={`${fields.testid?.value}-dropdown`}
                  id="address-picker"
                  value={selectedAddress}
                  onChange={handleAddressUpdate}
                  required={fields.required?.value}
                >
                  {fields.placeholder?.value && (
                    <option value="">{`${addresses?.entityAddressList?.length} ${fields.placeholder.value} ${postCode}`}</option>
                  )}
                  {defaultAddress &&
                    defaultAddress.map((o, i) => (
                      <option key={i} value={o}>
                        {o}
                      </option>
                    ))}
                </select>

                {hasSelectedAddress(selectedAddress, defaultAddress) && (
                  <div className="vds-form__group vds-form__group--limited-width">
                    <div className="vds-form__group">
                      {selectedAddress
                        .split(/,\s*/)
                        .filter(line => line.trim() !== '')
                        .map((line, index) => (
                          <React.Fragment key={index}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="vds-form__group vds-form__group--limited-width">
                <div className="vds-form__group">
                  <a
                    onClick={e => {
                      e.preventDefault();
                      setShowManualAddress(true);
                      setError(null);
                    }}
                    data-testid={`${fields.testid?.value}-link`}
                  >
                    {fields?.manualAddressLabel?.value}
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
        {hasInlineError(displayErrors, fields, error) && (
          <div className="vds-form__group vds-form__group--limited-width">
            <div className="vds-form__validation-message vds-form__validation-message--invalid">
              <span className="vds-form__validation-text">{error.message}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const hasSelectedAddress = function (selectedAddress, defaultAddress) {
  return selectedAddress && selectedAddress !== '' && defaultAddress?.includes(selectedAddress);
};

const hasInlineError = function (displayErrors, fields, error) {
  return displayErrors && fields.inlineError?.value && error;
};

const mapStateToProps = (state, props) => {
  const reduxValuePath = props.fields.reduxValuePath?.value;
  let defaultValue;
  if (reduxValuePath) {
    defaultValue = resolve(reduxValuePath, state);
  }
  return {
    ...props,
    defaultValue,
    displayErrors: state.appState.displayErrors,
    addresses: state.addressResults?.addressResults,
    postCode: state.quoteData?.prospectDetails.address.postCode,
  };
};
export default connect(mapStateToProps)(AddressPicker);
